import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { api, fmtINR } from "../api";
import { useCart } from "../CartContext";
import Seo from "../components/Seo";
import { Star, ShieldCheck, FlaskConical, Microscope, ArrowLeft, Box } from "lucide-react";

export default function ProductDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [qty, setQty] = useState(1);
    const [tab, setTab] = useState("description");
    const [view, setView] = useState({ type: "image", index: 0 });
    const [together, setTogether] = useState([]);
    const [showBuyBar, setShowBuyBar] = useState(false);
    const buyRowRef = useRef(null);
    const { add } = useCart();

    useEffect(() => {
        api.get(`/products/${id}`).then((r) => { setP(r.data); setView({ type: "image", index: 0 }); }).catch(() => setP(null));
        api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data || []));
        api.get(`/products/${id}/bought-together`).then((r) => setTogether(r.data || [])).catch(() => setTogether([]));
    }, [id]);

    useEffect(() => {
        if (!p || !buyRowRef.current) return;
        const obs = new IntersectionObserver(([entry]) => setShowBuyBar(!entry.isIntersecting), { threshold: 0 });
        obs.observe(buyRowRef.current);
        return () => obs.disconnect();
    }, [p]);

    useEffect(() => {
        document.body.style.setProperty("--fab-offset", showBuyBar ? "72px" : "0px");
        return () => document.body.style.setProperty("--fab-offset", "0px");
    }, [showBuyBar]);

    if (!p) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[var(--body)]">Loading product…</div>;

    const images = (p.images || []).filter((u) => !/youtube\.com|youtu\.be|vimeo\.com|\.mp4(\?|$)|\.webm(\?|$)|\.mov(\?|$)/i.test(u || ""));
    const has3D = !!p.model_3d_url;
    const hasVideo = !!p.video_url;
    const together_components = together.filter((t) => t.is_component);
    const separateTotal = together_components.reduce((s, t) => s + (t.price || 0), 0);
    const bundleSavings = together_components.length >= 2 ? Math.round(separateTotal - p.price) : 0;

    return (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-10" data-testid="product-detail">
            <Seo title={p.name} description={p.short_desc || (p.description || "").slice(0, 155)} image={(p.images || [])[0]} />
            <Link to="/shop" className="inline-flex items-center gap-1.5 text-[13px] text-[var(--body)] hover:text-[var(--ink)] mb-8 fast-link">
                <ArrowLeft size={14} /> Back to Shop
            </Link>
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
                <div>
                    <div className="prod-img mb-4 aspect-square overflow-hidden rounded-2xl bg-gradient-to-b from-[var(--bg-alt)] to-[var(--rose)]/30 grid place-items-center" data-testid="product-main-view">
                        {view.type === "3d" && has3D ? (
                            // @ts-ignore — model-viewer is a web component loaded from CDN
                            <model-viewer
                                src={p.model_3d_url}
                                camera-controls
                                auto-rotate
                                style={{ width: "100%", height: "100%" }}
                                exposure="1"
                            ></model-viewer>
                        ) : view.type === "video" && hasVideo ? (
                            <video src={p.video_url} controls autoPlay loop className="w-full h-full object-cover" />
                        ) : images[view.index] ? (
                            <img src={images[view.index]} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-[140px]">{p.emoji || "🌿"}</span>
                        )}
                    </div>
                    {(images.length > 1 || has3D || hasVideo) && (
                        <div className="flex gap-2 flex-wrap" data-testid="product-thumbs">
                            {images.map((src, i) => (
                                <button
                                    key={`${src}-${i}`}
                                    onClick={() => setView({ type: "image", index: i })}
                                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition ${view.type === "image" && view.index === i ? "border-[var(--taupe)]" : "border-[var(--line)] opacity-70 hover:opacity-100"}`}
                                    data-testid={`product-thumb-${i}`}
                                >
                                    <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }} />
                                </button>
                            ))}
                            {has3D && (
                                <button
                                    onClick={() => setView({ type: "3d", index: 0 })}
                                    className={`w-16 h-16 rounded-lg border-2 grid place-items-center transition ${view.type === "3d" ? "border-[var(--taupe)] bg-[var(--bg-alt)]" : "border-[var(--line)] hover:bg-[var(--bg-alt)]"}`}
                                    data-testid="product-thumb-3d"
                                    aria-label="3D view"
                                >
                                    <Box size={20} className="text-[var(--taupe)]" />
                                    <span className="absolute mt-12 text-[9px] uppercase tracking-wider">3D</span>
                                </button>
                            )}
                            {hasVideo && (
                                <button
                                    onClick={() => setView({ type: "video", index: 0 })}
                                    className={`w-16 h-16 rounded-lg border-2 grid place-items-center transition ${view.type === "video" ? "border-[var(--taupe)] bg-[var(--bg-alt)]" : "border-[var(--line)] hover:bg-[var(--bg-alt)]"}`}
                                    data-testid="product-thumb-video"
                                    aria-label="Video"
                                >
                                    <span className="text-[10px] uppercase tracking-wider">Video</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div>
                    <div className="overline mb-2">{p.category}</div>
                    <h1 className="h-display text-[36px] lg:text-[44px] mb-4">{p.name}</h1>
                    <div className="flex items-center gap-2 text-[14px] text-[var(--body)]">
                        <Star size={14} className="fill-[var(--gold)] text-[var(--gold)]" />
                        <span>{p.rating || 4.8}</span>
                        <span>·</span>
                        <span>{p.review_count || 0} reviews</span>
                    </div>
                    <p className="mt-5 text-[15px] text-[var(--body)] leading-relaxed">
                        {p.short_desc || p.description}
                    </p>
                    <div className="mt-6 flex items-baseline gap-3">
                        <span className="font-serif text-[36px] text-[var(--ink)]">{fmtINR(p.price)}</span>
                        {p.original_price > p.price && (
                            <>
                                <span className="text-[16px] text-[var(--body)] line-through">{fmtINR(p.original_price)}</span>
                                <span className="text-[12px] text-[var(--terra)] font-semibold">
                                    {Math.round((1 - p.price / p.original_price) * 100)}% off
                                </span>
                            </>
                        )}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--body)]">
                        {p.stock > 0 ? `In stock · ${p.stock} available` : "Out of stock"}
                    </div>
                    {bundleSavings > 0 && (
                        <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-green-700/10 text-green-800 px-3.5 py-1.5 rounded-full" data-testid="bundle-savings-badge">
                            🎁 You save {fmtINR(bundleSavings)} vs buying these separately ({fmtINR(separateTotal)})
                        </div>
                    )}
                    <div className="mt-7 flex items-center gap-3" ref={buyRowRef}>
                        <div className="qty">
                            <button onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
                            <span>{qty}</span>
                            <button onClick={() => setQty((q) => q + 1)}>+</button>
                        </div>
                        <button onClick={() => add(p, qty)} disabled={p.stock <= 0} className="btn btn-primary flex-1 disabled:opacity-50" data-testid="detail-add-to-cart">
                            Add to Cart
                        </button>
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-3">
                        <Pillar icon={<ShieldCheck size={14} />} t="Lab Tested" />
                        <Pillar icon={<Microscope size={14} />} t="Clinically Reviewed" />
                        <Pillar icon={<FlaskConical size={14} />} t="GMP Certified" />
                    </div>

                    <div className="mt-10 border-t border-[var(--line-soft)] pt-6">
                        <div className="flex gap-6 mb-5">
                            {["description", "reviews"].map((k) => (
                                <button
                                    key={k}
                                    onClick={() => setTab(k)}
                                    className={`text-[12px] uppercase tracking-[0.18em] pb-2 ${
                                        tab === k ? "text-[var(--ink)] border-b border-[var(--taupe)]" : "text-[var(--body)]"
                                    }`}
                                    data-testid={`tab-${k}`}
                                >
                                    {k} {k === "reviews" ? `(${reviews.length})` : ""}
                                </button>
                            ))}
                        </div>
                        {tab === "description" ? (
                            <p className="text-[14px] text-[var(--body)] leading-relaxed whitespace-pre-line">
                                {p.description || "Crafted with evidence-based botanicals."}
                            </p>
                        ) : (
                            <ReviewsList productId={p.id} reviews={reviews} setReviews={setReviews} />
                        )}
                    </div>
                </div>
            </div>

            {together.length > 0 && (
                <section className="mt-16 border-t border-[var(--line-soft)] pt-10" data-testid="bought-together">
                    <div className="mb-7">
                        <p className="eyebrow">Complete the ritual</p>
                        <h2 className="font-serif text-[26px] sm:text-[32px] text-[var(--ink)] mt-2">Frequently bought together</h2>
                        <p className="text-[13.5px] text-[var(--body)] mt-1">
                            The standalone products that pair with this kit — also available individually.
                            {bundleSavings > 0 && (
                                <span className="text-green-800 font-medium" data-testid="bundle-savings-note">
                                    {" "}Bought separately they cost {fmtINR(separateTotal)} — this kit saves you {fmtINR(bundleSavings)}.
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {together.map((t) => (
                            <div key={t.id} className="card-glass hover-float p-4 flex flex-col" style={{ borderRadius: 18 }} data-testid={`bought-together-item-${t.id}`}>
                                <Link to={`/product/${t.id}`} className="block">
                                    <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-b from-[var(--bg-alt)] to-[var(--rose)]/30 grid place-items-center mb-3">
                                        {(t.images || [])[0] ? (
                                            <img src={t.images[0]} alt={t.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-[64px]">{t.emoji || "🌿"}</span>
                                        )}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">{t.category}</div>
                                    <div className="prod-title text-[15px] leading-snug mt-1 text-[var(--ink)]">{t.name}</div>
                                </Link>
                                <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                                    <span className="font-serif text-[17px] text-[var(--ink)]">{fmtINR(t.price)}</span>
                                    <button
                                        onClick={() => add(t, 1)}
                                        disabled={t.stock <= 0}
                                        className="btn btn-outline btn-sm disabled:opacity-40"
                                        data-testid={`bought-together-add-${t.id}`}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {showBuyBar && createPortal(
                <div className="sticky-buy-bar lg:hidden" data-testid="mobile-buy-bar">
                    <div className="min-w-0 flex-1">
                        <div className="prod-title text-[12px] leading-tight truncate">{p.name}</div>
                        <div className="font-serif text-[18px] text-[var(--ink)]">{fmtINR(p.price)}</div>
                    </div>
                    <div className="qty qty-compact shrink-0">
                        <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">−</button>
                        <span>{qty}</span>
                        <button onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity">+</button>
                    </div>
                    <button onClick={() => add(p, qty)} disabled={p.stock <= 0} className="btn btn-primary shrink-0 disabled:opacity-50" style={{ padding: "12px 20px" }} data-testid="mobile-buy-bar-add">
                        {p.stock > 0 ? "Add to Cart" : "Out of stock"}
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}

function Pillar({ icon, t }) {
    return (
        <div className="card flex items-center gap-2 px-3 py-2.5 text-[12px] text-[var(--ink-soft)]">
            <span className="text-[var(--taupe)]">{icon}</span>
            <span>{t}</span>
        </div>
    );
}

function ReviewsList({ productId, reviews, setReviews }) {
    const [name, setName] = useState("");
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/reviews", { product_id: productId, rating, comment, name: name || "Anonymous" });
            setReviews([data, ...reviews]);
            setComment(""); setName(""); setRating(5);
        } finally { setSubmitting(false); }
    };

    return (
        <div>
            <form onSubmit={submit} className="card p-5 mb-6" data-testid="review-form">
                <div className="flex items-center gap-2 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setRating(n)} className={n <= rating ? "text-[var(--gold)]" : "text-[var(--line)]"}>
                            <Star size={20} fill="currentColor" />
                        </button>
                    ))}
                </div>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="field mb-2" />
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your experience…" className="field" required />
                <button className="btn btn-primary btn-sm mt-3" disabled={submitting} data-testid="submit-review-btn">
                    {submitting ? "Posting…" : "Post Review"}
                </button>
            </form>
            {reviews.length === 0 ? (
                <p className="text-[var(--body)] text-sm">Be the first to review this product.</p>
            ) : (
                <div className="space-y-4">
                    {reviews.map((r) => (
                        <div key={r.id} className="border-b border-[var(--line-soft)] pb-4">
                            <div className="flex items-center justify-between mb-1">
                                <div className="font-medium text-[14px] text-[var(--ink)]">{r.name}</div>
                                <div className="text-[var(--gold)] text-[13px]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                            </div>
                            <p className="text-[13px] text-[var(--body)]">{r.comment}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
